import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type Ref,
} from 'react';
import { action, makeObservable, observable, computed } from 'mobx';

/**
 * What a view searches, as the app's one search bar reads it (F-0743 §4, D-0431, VSB-32). The view
 * keeps its text in its own address (`useViewSearch`, VSB-4); the bar is where the reader types it.
 */
export interface IViewSearchScope {
  value: string;
  onChange: (query: string) => void;
  /** What the bar says it searches ("Search schedules"), when the view names it. */
  label?: string;
  placeholder?: string;
  /** Keys the view handles itself in its search: arrows into a tree, Escape over an empty one. */
  onKeyDown?: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
  /** Handed the bar's input, for a view that moves the caret into its search from elsewhere. */
  inputRef?: Ref<HTMLInputElement>;
}

/** One place a view's index finds (VSB-36): a setting, a device. */
export interface IViewSearchHit {
  id: string;
  label: string;
  /** Where it is, under its name ("Account · Personal"). */
  detail?: string;
  /** Why the reader cannot use it: listed disabled with this, never left out. */
  refused?: string;
  open: () => void;
  /** The name of the index that found it, which heads its group (I-2373). */
  group?: string;
}

/**
 * A view's search that finds places to jump to rather than narrowing a list (VSB-36): Settings'
 * index, the devices of Settings › Devices. The bar's text is its own; the hits are its first group.
 */
export interface IViewSearchIndex {
  /** What the bar heads this index's hits with ("Settings", "Devices"), in the reader's language. */
  label?: string;
  find: (query: string) => readonly IViewSearchHit[];
}

/**
 * The search scopes of the views on screen, newest last. The bar reads the newest: a view opened
 * over another (a panel's own list) is the content the reader is looking at.
 */
export class ViewSearchScopes {
  private entries: Array<{ id: number; scope: IViewSearchScope }> = [];
  /** The indexes of the views on screen, newest last (VSB-36). */
  indexes: Array<{ id: number; index: IViewSearchIndex }> = [];
  private nextId = 1;
  /**
   * How many of the view's things its search leaves, as its count line says them
   * (`ViewSearchSummary`), for the bar's first result (VSB-32). `null` while nothing is searched.
   */
  matches: { shown: number; total: number } | null = null;

  constructor() {
    makeObservable<ViewSearchScopes, 'entries'>(this, {
      entries: observable.shallow,
      indexes: observable.shallow,
      matches: observable.ref,
      current: computed,
      any: computed,
      add: action,
      update: action,
      remove: action,
      addIndex: action,
      removeIndex: action,
      reportMatches: action,
    });
  }

  /** The scope the bar searches, or `null` where no view on screen registered one. */
  get current(): IViewSearchScope | null {
    return this.entries.length > 0 ? this.entries[this.entries.length - 1].scope : null;
  }

  /**
   * Whether any view on screen registered a search. The shell reads this, not {@link current}, so a
   * keystroke (a new scope value) re-renders the bar alone, not the whole shell.
   */
  get any(): boolean {
    return this.entries.length > 0;
  }

  add(scope: IViewSearchScope): number {
    const id = this.nextId++;
    this.entries.push({ id, scope });
    return id;
  }

  update(id: number, scope: IViewSearchScope): void {
    const index = this.entries.findIndex((entry) => entry.id === id);
    if (index >= 0) this.entries[index] = { id, scope };
  }

  remove(id: number): void {
    this.entries = this.entries.filter((entry) => entry.id !== id);
  }

  addIndex(index: IViewSearchIndex): number {
    const id = this.nextId++;
    this.indexes.push({ id, index });
    return id;
  }

  removeIndex(id: number): void {
    this.indexes = this.indexes.filter((entry) => entry.id !== id);
  }

  /** What the indexes on screen find for `query`, the newest index first (VSB-36b). */
  hits(query: string): IViewSearchHit[] {
    return this.indexes
      .slice()
      .reverse()
      .flatMap((entry) =>
        entry.index.find(query).map((hit) => ({ ...hit, group: entry.index.label })),
      );
  }

  reportMatches(matches: { shown: number; total: number } | null): void {
    const same =
      matches === this.matches ||
      (matches !== null &&
        this.matches !== null &&
        matches.shown === this.matches.shown &&
        matches.total === this.matches.total);
    if (!same) this.matches = matches;
  }
}

/**
 * The shell's scopes and whether its bar is drawn (at the top of a large form factor, VSB-30, or a phone's list, VSB-35). Where it
 * is, a view's `ViewSearchField` registers its scope and draws nothing of its own; where it is not
 * (a phone's pushed screen, a screen with no shell), the view draws its own field as before.
 */
export interface IViewSearchBarContext {
  scopes: ViewSearchScopes;
  barShown: boolean;
}

export const ViewSearchBarContext = createContext<IViewSearchBarContext | null>(null);

export function useViewSearchBar(): IViewSearchBarContext | null {
  return useContext(ViewSearchBarContext);
}

/**
 * Registers `scope` with the shell's bar while the bar is at the top and the caller is on screen
 * (VSB-32), or does nothing: the view then draws its own field. `null` registers nothing (a view
 * whose search is not on screen yet). Registered before paint, so the bar reads the view's text in
 * the frame the view first draws. Returns whether the bar searches it.
 */
export function useViewSearchScope(scope: IViewSearchScope | null): boolean {
  const bar = useViewSearchBar();
  const scopes = bar?.barShown && scope ? bar.scopes : null;
  const id = useRef<number | null>(null);
  const latest = useRef(scope);
  latest.current = scope;
  useLayoutEffect(() => {
    if (!scopes || !latest.current) return undefined;
    id.current = scopes.add(latest.current);
    return () => {
      if (id.current !== null) scopes.remove(id.current);
      id.current = null;
    };
  }, [scopes]);
  // Kept current on every render: the text, and the handlers that close over the view's state.
  useLayoutEffect(() => {
    if (scopes && id.current !== null && latest.current) scopes.update(id.current, latest.current);
  });
  return scopes !== null;
}

/**
 * Registers a view's index with the shell's bar while the bar is shown (VSB-36), or does nothing:
 * the view then draws its own field. Returns whether the bar searches it. The index is read through
 * a ref, so a view's new answers need no new registration.
 */
export function useViewSearchIndex(index: IViewSearchIndex | null): boolean {
  const bar = useViewSearchBar();
  const scopes = bar?.barShown && index ? bar.scopes : null;
  const latest = useRef(index);
  latest.current = index;
  useLayoutEffect(() => {
    if (!scopes) return undefined;
    const id = scopes.addIndex({
      get label() {
        return latest.current?.label;
      },
      find: (query) => latest.current?.find(query) ?? [],
    });
    return () => scopes.removeIndex(id);
  }, [scopes]);
  return scopes !== null;
}

/**
 * Tells the shell's bar how many of the view's things its search leaves (VSB-32), while the bar is
 * at the top; `null` while nothing is searched. Withdrawn when the view leaves.
 */
export function useReportViewSearchMatches(matches: { shown: number; total: number } | null): void {
  const bar = useViewSearchBar();
  const scopes = bar?.barShown ? bar.scopes : null;
  const shown = matches?.shown ?? null;
  const total = matches?.total ?? null;
  useLayoutEffect(() => {
    if (!scopes) return undefined;
    scopes.reportMatches(shown === null || total === null ? null : { shown, total });
    return () => scopes.reportMatches(null);
  }, [scopes, shown, total]);
}
